import {
  ContentLayout,
  Table,
  Box,
  Pagination,
  PropertyFilter,
  SpaceBetween,
  Header,
  CollectionPreferences,
  CollectionPreferencesProps,
  Button,
  StatusIndicator,
  PropertyFilterProps,
} from '@cloudscape-design/components';
import { useLocalStorage } from '@uidotdev/usehooks';
import { useCallback, useEffect, useState, useMemo } from 'react';
import { useSplitPanel } from '../../context/SplitPanelContext';
import useUserProfile from 'src/hooks/useUserProfile';
import {
  getLocationsTableDefinitions,
  defaultContentDisplay,
  getFilterLocationsProperties,
  contentDisplayPreference,
  pageSizePreference,
  getExportLocationConfig,
} from './locationsConfig';
import { useUsers } from 'src/store/users/useUsers';
import { locationService } from 'src/data/api/helios-service-ts/locationService';
import { useQuery } from '@tanstack/react-query';
import { Location } from '@amzn/gbsa-helios-typescript-client';
import { useCollection } from '@cloudscape-design/collection-hooks';
import { Persona } from 'src/contstant/Persona';
import { ExportButton } from 'src/components/ExportButton';
import { checkFeaturePermission } from 'src/utils/generalUtils';
import permissionAccessConfig from 'src/contstant/permissionAccessConfig';

import { LocationDetailsFormUpdate } from 'src/components/location/LocationDetailsFormUpdate';
import { businessUnitService } from 'src/data/api/orbit-service-ts/businessUnitService';
import { LocationDetailsFormCreate } from 'src/components/location/LocationDetailsFormCreate';
import { LocationDetailsFormView } from 'src/components/location/LocationDetailsFormView';
import { useCollectionFilterPersistence } from 'src/hooks/useCollectionFilterPersistence';
import { getPageFilterKey } from 'src/contstant/storageKeys';

const LocationsPage = () => {
  const { setContent, setTitle, closeView } = useSplitPanel();
  const [selectedItems, setSelectedItems] = useState<Location[]>([]);

  const { getPersona, getUserProgramId, getUserBusinessUnitId, getDivisions, getUserId } =
    useUserProfile();
  const { userPermission, checkUserPermissions } = useUsers();
  const businessUnitId = getUserBusinessUnitId();

  // Memoize default filters to prevent unnecessary re-renders
  const defaultFilters = useMemo((): PropertyFilterProps.Query => {
    return {
      tokens: [],
      operation: 'and' as const,
    };
  }, []);

  // Memoize exclude fields to prevent unnecessary re-renders
  const excludeFields = useMemo(() => ['businessUnitId'], []);

  // Use collection filter persistence hook with business unit scoped key
  const { initialQuery, syncFilters } = useCollectionFilterPersistence({
    storageKey: getPageFilterKey(businessUnitId, 'locations'),
    defaultFilters,
    excludeFields,
    businessUnitId,
  });

  const {
    data: locationsList,
    isLoading: isLoadingLocations,
    refetch: refetchLocations,
  } = useQuery({
    queryKey: ['locations', businessUnitId],
    queryFn: () =>
      locationService
        .getAll({
          businessUnitId: businessUnitId,
          isEnabledForBU: undefined,
        })
        .then((res) => res.locations || []),
  });

  const [preferences, setPreferences] = useLocalStorage<
    CollectionPreferencesProps['preferences']
  >('location-page-preferences', {
    pageSize: 10,
    contentDisplay: defaultContentDisplay,
  });

  const {
    items,
    paginationProps,
    collectionProps,
    propertyFilterProps,
    filteredItemsCount,
    allPageItems,
  } = useCollection(locationsList || [], {
    pagination: {
      pageSize: preferences?.pageSize || 10,
    },
    propertyFiltering: {
      filteringProperties: getFilterLocationsProperties(
        getPersona() === Persona.PLATFORM_ADMIN,
      ),
      defaultQuery: initialQuery, // Use initial query from persistence hook
    },
    sorting: {
      defaultState: {
        sortingColumn: {
          sortingField: 'locationCode',
        },
        isDescending: false,
      },
    },
    selection: {
      trackBy: (item) => item.locationCode,
    },
  });

  // Sync filters with the persistence hook (doesn't cause re-renders)
  syncFilters(propertyFilterProps);

  const { data: businessUnitList } = useQuery({
    queryKey: ['businessUnits', getUserProgramId()],
    enabled: !!getUserProgramId(),
    initialData: [],
    queryFn: () => {
      return businessUnitService
        .getAll({
          programId: getUserProgramId(),
          isActive: true,
          isArchived: false,
        })
        .then((res) => res.businessUnits);
    },
  });
  const isEditLocation = useCallback(() => {
    return checkFeaturePermission(
      getPersona(),
      permissionAccessConfig.LOCATION.EDIT_LOCATION,
      userPermission?.permissions,
    );
  }, [userPermission?.permissions, getPersona()]);

  // Check user permissions when user or business unit changes
  useEffect(() => {
    if (getPersona() !== Persona.PLATFORM_ADMIN) {
      checkUserPermissions({
        userId: getUserId(),
        programId: getUserProgramId(),
        businessUnit: businessUnitId,
        permissions: [permissionAccessConfig.LOCATION.EDIT_LOCATION],
        divisions: getDivisions(),
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [businessUnitId]);

  // Close split panel when navigating away from this page
  useEffect(() => {
    return () => {
      closeView();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const openLocationDetailsView = (title: string, location?: Location | null) => {
    const editPermission = isEditLocation();
    if (!editPermission && location) {
      setTitle(`Location ${location.locationCode} Details`);
      setContent(
        <LocationDetailsFormView
          location={location}
          onViewDismiss={() => {
            setSelectedItems([]);
            closeView();
            refetchLocations(); // Refresh list
          }}
        />,
      );
      return;
    }
    if (!editPermission) return;
    setTitle(title);

    if (location) {
      setContent(
        <LocationDetailsFormUpdate
          key={location.locationCode}
          location={location}
          onViewDismiss={() => {
            setSelectedItems([]);
            closeView();
            refetchLocations(); // Refresh list
          }}
        />,
      );
      return;
    }
    setContent(
      <LocationDetailsFormCreate
        onViewDismiss={() => {
          setSelectedItems([]);
          closeView();
          refetchLocations(); // Refresh list
        }}
      />,
    );
  };
  return (
    <ContentLayout>
      <Box margin={{ top: 'xxxl' }}>
        <Table
          {...collectionProps}
          items={items}
          columnDefinitions={getLocationsTableDefinitions(businessUnitList)}
          columnDisplay={preferences?.contentDisplay}
          header={
            <Header
              actions={
                <SpaceBetween direction="horizontal" size="xs">
                  <Button
                    data-testid="refresh-button"
                    iconName="refresh"
                    onClick={() => {
                      setSelectedItems([]);
                      closeView();
                      refetchLocations();
                    }}
                  />
                  <ExportButton
                    data={Array.from(allPageItems ?? [])}
                    config={getExportLocationConfig(businessUnitList)}
                    onError={(error) => {
                      alert(error);
                    }}
                  />
                  {isEditLocation() && (
                    <Button
                      variant="normal"
                      data-testid="add-location-button"
                      onClick={() => openLocationDetailsView('Create Location', null)}
                    >
                      Create Location
                    </Button>
                  )}
                </SpaceBetween>
              }
            >
              Locations (
              {filteredItemsCount !== undefined
                ? filteredItemsCount
                : (locationsList?.length ?? 0)}
              )
            </Header>
          }
          preferences={
            <CollectionPreferences
              title="Preferences"
              confirmLabel="Confirm"
              cancelLabel="Cancel"
              preferences={preferences}
              contentDisplayPreference={contentDisplayPreference}
              pageSizePreference={pageSizePreference}
              onConfirm={({ detail }) => {
                setPreferences(detail);
              }}
            />
          }
          filter={
            <PropertyFilter
              {...propertyFilterProps}
              countText={`${filteredItemsCount} matches`}
              filteringPlaceholder="Filter Locations"
            />
          }
          empty={
            <Box textAlign="center" padding="m">
              <StatusIndicator type="warning">
                No locations matching the filter.
              </StatusIndicator>
            </Box>
          }
          pagination={<Pagination {...paginationProps} />}
          selectedItems={selectedItems}
          onSelectionChange={({ detail }) => {
            setSelectedItems(detail.selectedItems);
            const [item] = detail.selectedItems;
            if (!item) return;
            openLocationDetailsView('Update location', item);
          }}
          // selectionType={'single'}
          loading={isLoadingLocations}
          loadingText="Loading Locations..."
          selectionType="single"
        />
      </Box>
    </ContentLayout>
  );
};

export default LocationsPage;
